import React, { useState } from 'react';
import { LinkPreview } from '../../types';
import { resolveServerUrl } from '../../utils/serverUrl';
import { openExternalUrl } from '../../utils/openExternal';
import { isTauri } from '../../utils/tauri';

interface LinkPreviewCardProps {
  preview: LinkPreview;
  darkMode?: boolean;
}

/**
 * 링크 미리보기 카드 (슬랙형 세로 카드 — 썸네일 / 도메인 / 제목 / 설명).
 *
 * MessageItem이 직접 import한다. 렌더러를 prop으로 주입받는 방식(renderAttachmentBlock 등)을
 * 쓰지 않는 이유는 MessageItem의 memo 계약(MessageItem.tsx 하단 주석) 때문이다 —
 * 콜백 prop이 하나 늘 때마다 useCallback 체인이 길어지고, 하나라도 참조가 흔들리면
 * 입력창 타이핑 한 글자에 메시지 목록 전체가 리렌더된다. 여기서 필요한 것은
 * msg.linkPreview와 darkMode뿐이고 둘 다 이미 MessageItem이 갖고 있다.
 *
 * 색은 첨부 파일 카드(ChatWindow.renderAttachments)와 동일한 표면을 쓴다.
 * 브랜드 레드를 쓰지 않는 이유: brand-*는 액션·경고·에러·카운트를 뜻하므로
 * 미리보기에 쓰면 경고처럼 읽힌다. 포커스 링에만 최소로 쓴다.
 *
 * ⚠ z-* 클래스를 붙이지 말 것 — 선택 모드의 오버레이(MessageItem의 absolute inset-0 z-10)를
 *   뚫고 링크가 열린다. 지금은 오버레이가 클릭을 삼켜 선택 토글로 동작한다.
 */
const LinkPreviewCardComponent: React.FC<LinkPreviewCardProps> = ({ preview, darkMode }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !!preview.imagePath && !imageFailed;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Tauri는 기본 브라우저로 넘긴다 (앱 WebView에서 열리면 돌아올 방법이 없다)
    if (isTauri()) {
      e.preventDefault();
      void openExternalUrl(preview.url);
    }
  };

  return (
    <a
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className={`block w-[280px] sm:w-[320px] max-w-full overflow-hidden rounded-xl border shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 ${
        darkMode
          ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
          : 'bg-white border-slate-200 hover:bg-slate-50'
      }`}
    >
      {showImage && (
        <img
          // 서버가 사내에 저장한 루트 상대경로다. Tauri는 origin이 tauri.localhost라
          // resolveServerUrl 없이는 404가 난다(2026-08 아바타 사고와 같은 함정).
          src={resolveServerUrl(preview.imagePath as string)}
          // 바로 아래에 제목이 있으므로 스크린리더가 두 번 읽지 않게 한다
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          onError={() => setImageFailed(true)}
          className={`w-full aspect-[1.91/1] object-cover ${darkMode ? 'bg-gray-700' : 'bg-slate-100'}`}
        />
      )}
      <div className="px-3 py-2.5 space-y-1">
        <div className={`text-xs truncate ${darkMode ? 'text-gray-500' : 'text-slate-500'}`}>
          {preview.siteName || preview.host}
        </div>
        {preview.title && (
          <div
            className={`text-sm font-semibold line-clamp-2 break-anywhere ${
              darkMode ? 'text-gray-100' : 'text-slate-800'
            }`}
          >
            {preview.title}
          </div>
        )}
        {preview.description && (
          <div
            className={`text-xs leading-relaxed line-clamp-2 break-anywhere ${
              darkMode ? 'text-gray-400' : 'text-slate-500'
            }`}
          >
            {preview.description}
          </div>
        )}
      </div>
    </a>
  );
};

export const LinkPreviewCard = React.memo(LinkPreviewCardComponent);
